export class Loader {
	static #instance;

	#loaders = new Map(); // Храним ID => { loaderElement, parentElement, resizeObserver }
	#scrollHandlers = new Map();

	constructor() {
		if (Loader.#instance) {
			return Loader.#instance;
		}

		Loader.#instance = this;
	}

	static getInstance() {
		return Loader.#instance || new Loader();
	}

	/**
	 * Генерирует уникальный ID для лоадера
	 * @returns {string}
	 */
	createId() {
		return `loader-${crypto.randomUUID?.() || Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
	}

	/**
	 * Вычисляет оптимальный размер спиннера на основе размеров контейнера
	 * @param {HTMLElement} container 
	 * @returns {number}
	 */
	#calculateSpinnerSize(container) {
		const rect = container.getBoundingClientRect();
		// const minDimension = Math.min(rect.width, rect.height);
		const minDimension = rect.width;
		
		// Спиннер должен быть 8-12% от минимального измерения контейнера
		let size = Math.floor(minDimension * 0.3);
		
		// Ограничиваем размер в разумных пределах
		size = Math.max(24, Math.min(80, size));
		
		return size;
	}

	/**
	 * Вычисляет позицию спиннера внутри контейнера с учетом видимости
	 * @param {HTMLElement} container 
	 * @returns {Object}
	 */
	#calculateSpinnerPosition(container) {
		const containerRect = container.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// Определяем видимую область контейнера относительно viewport
		const visibleRect = {
			left: Math.max(0, containerRect.left),
			top: Math.max(0, containerRect.top),
			right: Math.min(viewportWidth, containerRect.right),
			bottom: Math.min(viewportHeight, containerRect.bottom)
		};

		// Если контейнер полностью за пределами viewport
		if (visibleRect.left >= visibleRect.right || visibleRect.top >= visibleRect.bottom) {
			// Позиционируем спиннер по центру контейнера
			return {
				left: containerRect.width / 2,
				top: containerRect.height / 2,
				transform: 'translate(-50%, -50%)'
			};
		}

		// Вычисляем центр видимой области относительно контейнера
		const visibleCenterX = (visibleRect.left + visibleRect.right) / 2 - containerRect.left;
		const visibleCenterY = (visibleRect.top + visibleRect.bottom) / 2 - containerRect.top;

		// Ограничиваем позицию спиннера границами контейнера
		const spinnerSize = this.#calculateSpinnerSize(container);
		const minX = spinnerSize / 2;
		const minY = spinnerSize / 2;
		const maxX = containerRect.width - spinnerSize / 2;
		const maxY = containerRect.height - spinnerSize / 2;

		const finalX = Math.max(minX, Math.min(maxX, visibleCenterX));
		const finalY = Math.max(minY, Math.min(maxY, visibleCenterY));

		return {
			left: finalX,
			top: finalY,
			transform: 'translate(-50%, -50%)'
		};
	}

	/**
	 * Создает элемент лоадера
	 * @param {string} id - ID лоадера
	 * @param {Object} [options={}] - Опции: color, transparent
	 * @returns {HTMLElement}
	 */
	createLoaderElement(id, options = {}) {
		const { 
			color = 'primary', 
			transparent = false 
		} = options;

		const loader = document.createElement('div');
		loader.id = id;
		loader.classList.add('fm-loader');
		
		// Опциональная прозрачность фона
		if (transparent) {
			loader.style.backgroundColor = 'transparent';
		}

		const wrapper = document.createElement('div');
		wrapper.classList.add('fm-loader__spinner');

		const spinner = document.createElement('div');
		spinner.classList.add('spinner-border', `text-${color}`);

		wrapper.appendChild(spinner);
		loader.appendChild(wrapper);

		return loader;
	}

	/**
	 * Позиционирует и масштабирует спиннер внутри лоадера
	 * @param {string} id 
	 */
	#updateSpinnerPosition(id) {
		const loaderData = this.#loaders.get(id);
		if (!loaderData) return;

		const { loaderElement, parentElement } = loaderData;
		const spinnerElement = loaderElement.querySelector('.fm-loader__spinner');
		if (!spinnerElement) return;

		const position = this.#calculateSpinnerPosition(parentElement);
		const spinnerSize = this.#calculateSpinnerSize(parentElement);

		// Устанавливаем позицию спиннера внутри fm-loader
		spinnerElement.style.left = `${position.left}px`;
		spinnerElement.style.top = `${position.top}px`;
		spinnerElement.style.transform = position.transform;

		// Устанавливаем размер спиннера
		const spinner = spinnerElement.querySelector('.spinner-border');
		if (spinner) {
			spinner.style.width = `${spinnerSize}px`;
			spinner.style.height = `${spinnerSize}px`;
		}
	}

	/**
	 * Добавляет обработчики для отслеживания изменений
	 * @param {string} id 
	 * @param {HTMLElement} container 
	 */
	#addEventHandlers(id, container) {
		let rafId = null;
		
		// Обработчик скролла с throttling через requestAnimationFrame
		const scrollHandler = () => {
			if (rafId) return; // Пропускаем если уже запланировано обновление
			
			rafId = requestAnimationFrame(() => {
				if (this.#loaders.has(id)) {
					this.#updateSpinnerPosition(id);
				}
				rafId = null;
			});
		};

		// Добавляем обработчики скролла на все родительские элементы и window
		const addScrollListeners = (element) => {
			let currentElement = element;
			while (currentElement && currentElement !== document.body) {
				currentElement.addEventListener('scroll', scrollHandler, { passive: true });
				currentElement = currentElement.parentElement;
			}
			window.addEventListener('scroll', scrollHandler, { passive: true });
			window.addEventListener('resize', scrollHandler, { passive: true });
		};

		addScrollListeners(container);
		this.#scrollHandlers.set(id, {
			handler: scrollHandler,
			container: container,
			rafId: rafId
		});

		// ResizeObserver для отслеживания изменения размеров контейнера
		if (window.ResizeObserver) {
			const resizeObserver = new ResizeObserver(() => {
				scrollHandler(); // Используем тот же throttled handler
			});

			resizeObserver.observe(container);
			
			// Сохраняем observer в данных лоадера
			const loaderData = this.#loaders.get(id);
			if (loaderData) {
				loaderData.resizeObserver = resizeObserver;
			}
		}
	}

	/**
	 * Удаляет все обработчики событий
	 * @param {string} id 
	 */
	#removeEventHandlers(id) {
		const scrollData = this.#scrollHandlers.get(id);
		if (scrollData) {
			const { handler, container, rafId } = scrollData;
			
			// Отменяем pending requestAnimationFrame если есть
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
			
			// Удаляем обработчики скролла
			let currentElement = container;
			while (currentElement && currentElement !== document.body) {
				currentElement.removeEventListener('scroll', handler);
				currentElement = currentElement.parentElement;
			}
			window.removeEventListener('scroll', handler);
			window.removeEventListener('resize', handler);
			
			this.#scrollHandlers.delete(id);
		}

		// Отключаем ResizeObserver
		const loaderData = this.#loaders.get(id);
		if (loaderData && loaderData.resizeObserver) {
			loaderData.resizeObserver.disconnect();
		}
	}

	show(element, options = {}) {
		const targetElement = typeof element === 'string'
			? document.querySelector(element)
			: element;

		if (!(targetElement instanceof HTMLElement)) {
			throw new Error('Invalid element provided to Loader.show()');
		}

		// Проверяем, есть ли уже активный лоадер в этом элементе
		const existingLoaderId = targetElement.dataset.fmLoader;
		if (existingLoaderId && this.#loaders.has(existingLoaderId)) {
			return existingLoaderId; // Возвращаем существующий ID
		}

		const id = this.createId();
		const loaderElement = this.createLoaderElement(id, options);

		// Сохраняем в карте
		this.#loaders.set(id, { 
			loaderElement, 
			parentElement: targetElement,
			resizeObserver: null
		});

		// Добавляем лоадер в контейнер
		

		// Делаем контейнер относительным для правильного позиционирования
		const computedStyle = getComputedStyle(targetElement);
		if (computedStyle.position === 'static') {
			targetElement.classList.add('fm-loader-wrapper');
		}

		// Устанавливаем начальную позицию спиннера
		this.#updateSpinnerPosition(id);

		targetElement.appendChild(loaderElement);
		targetElement.dataset.fmLoader = id;

		// Добавляем обработчики событий
		this.#addEventHandlers(id, targetElement);

		return id;
	}

	hide(id) {
		const loaderData = this.#loaders.get(id);
		if (!loaderData) {
			console.warn(`Loader with id "${id}" does not exist`);
			return false;
		}

		const { loaderElement, parentElement } = loaderData;

		// Удаляем обработчики событий
		this.#removeEventHandlers(id);

		// Удаляем DOM-элемент с анимацией
		if (loaderElement && loaderElement.parentNode) {
			const spinner = loaderElement.querySelector('.fm-loader__spinner');
			// if (spinner) {
			// 	spinner.style.animation = 'spinnerFadeOut 0.2s ease-in-out';
			// }
			// loaderElement.style.animation = 'fadeOut 0.2s ease-in-out';
			
			// setTimeout(() => {
				if (loaderElement.parentNode) {
					loaderElement.remove();
				}
			// }, 200);
		}

		// Удаляем из карты
		this.#loaders.delete(id);

		// Очищаем dataset и классы
		if (parentElement) {
			// Проверяем, нет ли других активных лоадеров в этом элементе
			const hasOtherLoaders = [...this.#loaders.values()]
				.some(data => data.parentElement === parentElement);
			
			if (!hasOtherLoaders) {
				parentElement.classList.remove('fm-loader-wrapper');
				parentElement.removeAttribute('data-fm-loader');
			}
		}

		return true;
	}

	/**
	 * Скрывает лоадер по элементу-контейнеру
	 * @param {HTMLElement|string} element 
	 * @returns {boolean}
	 */
	hideByElement(element) {
		const targetElement = typeof element === 'string'
			? document.querySelector(element)
			: element;

		if (!(targetElement instanceof HTMLElement)) {
			return false;
		}

		const loaderId = targetElement.dataset.fmLoader;
		if (loaderId) {
			return this.hide(loaderId);
		}

		return false;
	}

	/**
	 * Скрывает последний созданный лоадер
	 */
	hideLast() {
		const ids = [...this.#loaders.keys()];
		if (ids.length === 0) return false;

		const lastId = ids[ids.length - 1];
		return this.hide(lastId);
	}

	/**
	 * Скрывает все активные лоадеры
	 */
	hideAll() {
		const ids = [...this.#loaders.keys()];
		ids.forEach(id => this.hide(id));
		return ids.length;
	}

	/**
	 * Проверяет, есть ли активный лоадер
	 * @param {string} [id] - ID конкретного лоадера
	 * @returns {boolean}
	 */
	isActive(id = null) {
		if (id) {
			return this.#loaders.has(id);
		}
		return this.#loaders.size > 0;
	}

	/**
	 * Принудительно обновляет позицию всех активных спиннеров
	 */
	updateAllPositions() {
		for (const id of this.#loaders.keys()) {
			this.#updateSpinnerPosition(id);
		}
	}

	/**
	 * Асинхронный оборачиватель: показывает лоадер, выполняет промис, скрывает лоадер
	 * @param {HTMLElement|string} element
	 * @param {Function} asyncFn - Промис или функция, возвращающая промис
	 * @param {Object} [options={}]
	 * @returns {Promise<*>}
	 */
	async withLoader(element, asyncFn, options = {}) {
		const id = this.show(element, options);
		try {
			return await asyncFn();
		} catch (error) {
			// Перебрасываем ошибку дальше
			throw error;
		} finally {
			this.hide(id);
		}
	}
}